/**
 * Inline retry block for a failed region (§11.2: "per-region load failures render inline retry
 * blocks"). Wire `onRetry` straight to the failing query's `refetch`.
 */

import type { ReactNode } from 'react';
import { describeError } from '../api/client';
import { Spinner } from './Spinner';
import { cx } from './cx';

export type ErrorStateProps = {
  title?: string;
  /** The query error; its message is rendered as-is (the server owns the words). */
  error?: unknown;
  /** Overrides the message derived from `error`. */
  description?: ReactNode;
  onRetry?: () => void;
  /** Usually the query's `isFetching`. */
  retrying?: boolean;
  retryLabel?: string;
  className?: string;
};

export function ErrorState({
  title = "Couldn't load this",
  error,
  description,
  onRetry,
  retrying = false,
  retryLabel = 'Retry',
  className,
}: ErrorStateProps) {
  const message = description ?? (error === undefined ? null : describeError(error));
  return (
    <div className={cx('error-state', className)} role="alert">
      <p className="error-state__title">{title}</p>
      {message ? <p className="error-state__description">{message}</p> : null}
      {onRetry ? (
        <div className="error-state__action">
          <button type="button" className="table-action" onClick={onRetry} disabled={retrying}>
            {retrying ? <Spinner size="sm" /> : null}
            <span className="table-action__label">{retryLabel}</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default ErrorState;
